import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QuickQuestions from './QuickQuestions';

const MessageBubble = ({ message, index }) => {
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`flex gap-2 sm:gap-3 md:gap-4 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4 sm:mb-5 md:mb-6 px-2 sm:px-0`}
    >
      {/* Avatar */}
      <motion.div 
        whileHover={{ scale: 1.1, rotate: 5 }}
        className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg sm:rounded-xl flex items-center justify-center ${
          isUser 
            ? 'bg-gradient-to-br from-purple-500 to-blue-500' 
            : 'bg-gradient-to-br from-green-500 to-cyan-500'
        } shadow-lg`}
      >
        {isUser ? (
          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        )}
      </motion.div>

      {/* Message Content */}
      <motion.div
        whileHover={{ scale: 1.01 }}
        className={`flex-1 max-w-3xl ${isUser ? 'text-right' : 'text-right'}`}
      >
        <div className={`inline-block px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 md:py-3 rounded-xl sm:rounded-2xl shadow-md ${
          isUser 
            ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white' 
            : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
        }`}>
          <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        </div>
        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-2 inline-block">
          {message.timestamp}
        </span>
      </motion.div>
    </motion.div>
  );
};

const ChatArea = ({ messages, isTyping, onQuickQuestionClick }) => {
  return (
  <div className="flex-1 overflow-y-auto px-4 sm:px-6 pt-4 pb-28 sm:pb-6">
      <div className="max-w-5xl mx-auto">
        <AnimatePresence>
          {messages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center h-full py-4 sm:py-6 md:py-8 px-4"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                  y: [0, -10, 0]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="mb-3 sm:mb-4"
              >
                <img 
                  src="/metalogo.png" 
                  alt="متافلای" 
                  className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl"
                />
              </motion.div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1.5 sm:mb-2 text-center px-4">
                سلام! چطور می‌تونم کمکتون کنم؟
              </h2>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 text-center max-w-md mb-4 sm:mb-5 md:mb-6 px-4">
                من دستیار هوش مصنوعی سفر شما هستم. می‌تونید درباره مقاصد گردشگری، هتل‌ها، رستوران‌ها و هر چیزی راجع به سفرتون از من بپرسید!
              </p>
              
              {/* Quick Questions */}
              <QuickQuestions onQuestionClick={onQuickQuestionClick} />
            </motion.div>
          ) : (
            <>
              {messages.map((message, index) => (
                <MessageBubble key={message.id} message={message} index={index} />
              ))}
              
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-4 mb-6"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-green-500 to-cyan-500 shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="bg-white dark:bg-gray-800 px-5 py-3 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                          className="w-2 h-2 bg-gray-400 rounded-full"
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChatArea;
